/**
 * Performance monitoring and optimization utilities
 */
import React from 'react'; 
import { log } from './environment.js';

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.reportingEnabled = true;
  }

  start(metricName) {
    if (!this.reportingEnabled) return;
    
    this.metrics.set(metricName, {
      startTime: performance.now(),
      endTime: null,
      duration: null
    });
  }

  end(metricName) {
    if (!this.reportingEnabled) return null;
    
    const metric = this.metrics.get(metricName);
    if (!metric) {
      log.warn(`Metric "${metricName}" not found`);
      return null;
    }
    
    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;
    
    this.report(metricName, metric);
    return metric.duration;
  }

  report(metricName, metric) {
    if (metric.duration > 100) { // Warn for slow operations
      log.warn(`Slow operation detected: ${metricName} took ${metric.duration.toFixed(2)}ms`);
    } else {
      log.debug(`Performance: ${metricName} took ${metric.duration.toFixed(2)}ms`);
    }
  }

  measure(metricName, fn) {
    this.start(metricName);
    try {
      return fn();
    } finally {
      this.end(metricName);
    }
  }

  async measureAsync(metricName, asyncFn) {
    this.start(metricName);
    try {
      return await asyncFn();
    } finally {
      this.end(metricName);
    }
  }

  enableReporting() {
    this.reportingEnabled = true;
  }

  disableReporting() {
    this.reportingEnabled = false;
  }

  clear() {
    this.metrics.clear();
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// React performance utilities
export const withPerformanceMonitoring = (WrappedComponent, componentName) => {
  return function WithPerformanceMonitoring(props) {
    const startTime = React.useRef(performance.now());
    
    React.useEffect(() => {
      const mountTime = performance.now() - startTime.current;
      performanceMonitor.report(`${componentName}_mount`, {
        startTime: startTime.current,
        endTime: performance.now(),
        duration: mountTime
      });
    }, []);
    
    return <WrappedComponent {...props} />;
  };
};

// Debounce utility for performance
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const context = this;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

// Throttle utility for performance
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Memoization utility
export const memoize = (fn) => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

export default {
  performanceMonitor,
  withPerformanceMonitoring,
  debounce,
  throttle,
  memoize,
};